import type { RegistroDeAuditoria } from '@/lib/supabase/tipos-bd'

/**
 * CÓMO SE DICE CADA REGISTRO, EN PALABRAS DE MEGA.
 *
 * La bitácora guarda «cambio_estado · orden_trabajo · creada → programada».
 * Eso es para la base; quien audita quiere leer «programó la orden de
 * trabajo OT-2026-0061». Johan lo pidió así: «quién programa, quién acepta
 * cotización, quién cierra». Así que las transiciones que tienen nombre en la
 * empresa se dicen con su nombre, y el resto con la forma general «pasó de
 * … a …».
 */

const DOCUMENTO: Record<RegistroDeAuditoria['documento_tipo'], string> = {
  orden_servicio: 'la orden de servicio',
  cotizacion: 'la cotización',
  orden_trabajo: 'la orden de trabajo',
}

/** «en_ejecucion» → «en ejecución»: los estados, como se leen. */
const ESTADO: Record<string, string> = {
  borrador: 'borrador',
  pendiente: 'pendiente',
  cotizada: 'cotizada',
  enviada: 'enviada al cliente',
  aprobada: 'aprobada',
  rechazada: 'rechazada',
  fallida: 'fallida',
  creada: 'creada',
  pendiente_cotizar: 'pendiente de cotizar',
  anulada: 'anulada',
  programada: 'programada',
  en_ejecucion: 'en ejecución',
  en_precierre: 'en precierre',
  cerrada: 'cerrada',
  finalizada: 'finalizada',
}

export const estadoLegible = (e: string | null) => (e ? ESTADO[e] ?? e.replace(/_/g, ' ') : '')

/**
 * Las transiciones con nombre propio en la operación. La llave es
 * «documento:anterior→nuevo»; si no está, se cae a la forma general.
 */
const CON_NOMBRE: Record<string, string> = {
  'orden_trabajo:creada→programada': 'programó',
  'orden_trabajo:programada→en_ejecucion': 'empezó',
  'orden_trabajo:en_ejecucion→en_precierre': 'terminó y mandó a revisión',
  'orden_trabajo:en_precierre→cerrada': 'cerró',
  'orden_trabajo:en_precierre→en_ejecucion': 'le devolvió al técnico',
  'orden_trabajo:cerrada→finalizada': 'finalizó',
  'cotizacion:cotizada→enviada': 'le envió al cliente',
  'cotizacion:enviada→aprobada': 'registró la aceptación del cliente de',
  'cotizacion:cotizada→aprobada': 'registró la aceptación del cliente de',
  'cotizacion:borrador→cotizada': 'generó',
  'cotizacion:borrador→pendiente': 'mandó a World Office',
}

/** La frase principal: el verbo y el documento. El número va aparte, como enlace. */
export function frase(r: RegistroDeAuditoria): { verbo: string; documento: string } {
  const doc = DOCUMENTO[r.documento_tipo]
  const llave = `${r.documento_tipo}:${r.estado_anterior}→${r.estado_nuevo}`

  if (r.estado_nuevo === 'anulada') return { verbo: 'anuló', documento: doc }
  if (CON_NOMBRE[llave]) return { verbo: CON_NOMBRE[llave], documento: doc }

  switch (r.tipo) {
    case 'creacion':
      return { verbo: 'creó', documento: doc }
    case 'aprobacion':
      return { verbo: 'aprobó', documento: doc }
    case 'rechazo':
      return { verbo: 'rechazó', documento: doc }
    case 'asignacion':
      return {
        verbo: r.documento_tipo === 'orden_trabajo' ? 'programó / asignó' : 'asignó',
        documento: doc,
      }
    case 'cambio_estado':
      return {
        verbo:
          r.estado_anterior && r.estado_nuevo
            ? `pasó de «${estadoLegible(r.estado_anterior)}» a «${estadoLegible(r.estado_nuevo)}»`
            : 'cambió el estado de',
        documento: doc,
      }
    default:
      return { verbo: 'anotó en', documento: doc }
  }
}

/** Las acciones del filtro, como se le dicen a una persona. */
export const ACCIONES: [RegistroDeAuditoria['tipo'], string][] = [
  ['creacion', 'Creó'],
  ['cambio_estado', 'Cambió estado'],
  ['aprobacion', 'Aprobó'],
  ['rechazo', 'Rechazó'],
  ['asignacion', 'Asignó / programó'],
  ['nota', 'Nota'],
]

export const DOCUMENTOS: [RegistroDeAuditoria['documento_tipo'], string][] = [
  ['orden_servicio', 'Orden de servicio'],
  ['cotizacion', 'Cotización'],
  ['orden_trabajo', 'Orden de trabajo'],
]
